import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

import { CalendlyWidget, IPrefill, Modal, Textarea } from 'shared-components';
import { EventBookingStatus, ICalendlyEvent, IMessage, IScheduledEvent } from 'api-paths-cas';

import { getDisplayName } from '../../../../utilities/formatting';
import { ApplicationState } from '../../../../store';
import { UNI_ROLES, ENROLY_AGENT_ROLES, AGENT_ROLES, UserRole } from '../../../../store/auth/types';

export interface IProps {
  interview?: ICalendlyEvent;
  event?: IScheduledEvent;
  eventKey?: string;
  studentDetails: IPrefill;
  userRole: UserRole;
  onSendMessage: (message: IMessage) => void;
  onCancelRequest: (eventType: string, eventKey: string, note: string) => void;
  onClose: () => void;
}

const CancelInterview = (props: IProps) => {
  const { interview, event, eventKey, studentDetails, userRole, onSendMessage, onCancelRequest, onClose } = props;

  const { auth, config } = useSelector(({ auth, config }: ApplicationState) => ({ auth, config }), shallowEqual);

  const userName = auth.student_number! || 'Anonymous';
  const displayName = getDisplayName(auth, config);
  const initialMessage: IMessage = {
    message: '',
    isSendToStudent: false,
    messageType: 'note',
    createdAt: 0,
    author: userName,
    authorName: displayName || userName,
    messageFrom: 'uni',
    messageTo: 'uni',
  };

  const [message, setMessage] = useState({ ...initialMessage });
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  const cancelRequest = () => {
    if (!message || !cancelConfirmed) {
      return;
    }

    onCancelRequest(interview?.eventType ?? '', eventKey ?? '', message.message);

    const msgReason = message.message ? `: ${message.message}` : '';
    const newMessage: IMessage = {
      ...initialMessage,
      message: `Interview <${interview?.name ?? ''}> Cancelled${msgReason}`,
      createdAt: Date.now(),
      ...(UNI_ROLES.includes(userRole) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(userRole) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(userRole) ? { agentRead: true } : {}),
    };
    onSendMessage(newMessage);

    onClose();
  };

  return (
    <Modal
      id="cancel-interview"
      show={true}
      title={'Cancel interview booking'}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={onClose}>Don't cancel</Button>

          <Button id="cancel-interview-button" onClick={cancelRequest} isDisabled={!cancelConfirmed} variant={'solid'}>
            Confirm cancellation
          </Button>
        </ButtonGroup>
      }
    >
      {event?.bookingStatus === EventBookingStatus.REQUESTED ? (
        <>
          <Textarea
            id={'cancel-reason-message'}
            label="Cancellation reason"
            value={message.message}
            onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
              setMessage({ ...message, message: event.currentTarget.value })
            }
            placeholder={'Type your message here...'}
            mb={6}
          />

          <Checkbox
            id="confirm-cancel-action"
            isChecked={cancelConfirmed}
            onChange={() => setCancelConfirmed(!cancelConfirmed)}
            size={'lg'}
          >
            I understand that this interview will be cancelled
          </Checkbox>
        </>
      ) : (
        <CalendlyWidget url={event?.cancelUrl ?? ''} prefill={studentDetails} onClose={onClose} />
      )}
    </Modal>
  );
};

export default CancelInterview;
